import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ScriptEditorComponent } from "../../../helper/script-editor/script-editor.component";
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { ListItemButtonComponent } from "../../../helper/script-editor/list-item-button/list-item-button.component";
import { ScriptService } from "../../../service/script.service";
import { AccountService } from "../../../service/account.service";
import { MatIconModule } from '@angular/material/icon';
import { AuthorCommentDialogComponent, AuthorCommentDialogData, AuthorCommentDialogResult } from "../../../helper/script-editor/popups/author-comment-dialog/author-comment-dialog.component";
import { ScriptSubmissionErrorComponent, ScriptSubmissionErrorData } from "../../../dialog/script-submission-error/script-submission-error.component";
import { ScriptSunsetBannerComponent } from "../../../helper/script-sunset-banner/script-sunset-banner.component";

@Component({
  selector: 'app-submit',
  imports: [
    CommonModule,
    ScriptEditorComponent,
    MatCardModule,
    MatListModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    FormsModule,
    ListItemButtonComponent,
    MatIconModule,
    ScriptSunsetBannerComponent
],
  templateUrl: './submit.component.html',
  styleUrl: './submit.component.less'
})
export class SubmitComponent {
  @ViewChild(ScriptEditorComponent) scriptEditor!: ScriptEditorComponent;
  
  route = inject(ActivatedRoute);
  router = inject(Router);
  scriptService = inject(ScriptService);
  accountService = inject(AccountService);
  snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  
  scriptId?: number;
  versionId?: number;
  isSubmitting = false;
  isSaving = false;
  isReadOnly = false;
  versions: any[] = [];
  selectedVersion?: number;
  isLoadingVersions = false;

  // Getter to access validation errors from the rich text editor
  get hasValidationErrors(): boolean {
    return this.scriptEditor?.richTextEditor?.errors?.length > 0;
  }

  get isCreationAllowed(): boolean {
    return ScriptService.isCreationAllowed() || this.accountService.account.value?.staff === true;
  }

  get isEditAllowed(): boolean {
    return ScriptService.isEditAllowed() || this.accountService.account.value?.staff === true;
  }

  get canSubmitCurrent(): boolean {
    if (this.scriptId && this.scriptId !== 0) {
      if (this.isLoadingVersions) return false;
      // If the script only has drafts, it's subject to the creation deadline
      const isOnlyDrafts = this.versions.length > 0 && this.versions.every(v => v.is_draft);
      if (isOnlyDrafts) {
        return this.isCreationAllowed;
      }
      return this.isEditAllowed;
    }
    return this.isCreationAllowed;
  }

  ngOnInit() {
    this.accountService.account.subscribe(account => {
      if (account !== undefined && (account === null || !account.staff) && !this.isReadOnly) {
        this.router.navigate(['/scripts/sunsetting-info']);
      }
    });

    this.route.params.subscribe(params => {
      this.scriptId = params['script_id'] ? +params['script_id'] : undefined;
      this.versionId = params['version_id'] ? +params['version_id'] : undefined;
      
      if (this.scriptId) {
        this.loadVersions();
      }
    });

    this.route.data.subscribe(data => {
      if (data['readonly']) {
        this.isReadOnly = true;
      }
      if (data['example']) {
        this.scriptId = 0; // Dummy ID
      }
    });
  }

  loadVersions() {
    if (!this.scriptId) return;
    
    this.isLoadingVersions = true;
    try {
      this.scriptService.getScriptVersions(this.scriptId).subscribe((versions: any[]) => {
        this.versions = versions;
      });
      this.selectedVersion = this.versionId || this.versions[0]?.version;
    } catch (error) {
      console.error('Failed to load versions:', error);
      this.showError('Failed to load version history');
    } finally {
      this.isLoadingVersions = false;
    }
  }

  onVersionChange() {
    if (this.selectedVersion && this.scriptId) {
      // Navigate to the selected version
      this.router.navigate(['/scripts/submit', this.scriptId, this.selectedVersion]);
    }
  }

  getVersionLabel(version: any): string {
    const date = new Date(version.created_at).toLocaleDateString();
    const status = this.getVersionStatus(version);
    return `Version ${version.version} - ${date} (${status})`;
  }

  getVersionStatus(version: any): string {
    if (version.is_draft) return 'Draft';
    if (version.status & 8) return 'Published';
    if (version.status & 4) return 'Rejected';
    if (version.status & 1) return 'Pending';
    return 'Superseeded';
  }

  submitChanges() {
    if (!this.scriptEditor) {
      this.showError('Script editor not ready. Please wait a moment and try again.');
      return;
    }

    if (this.hasValidationErrors) {
      const errors = this.scriptEditor.richTextEditor.errors.map(e => e.tooltip);
      this.showValidationErrorDialog('Please fix the following issues before submitting:', errors);
      return;
    }

    if (!this.canSubmitCurrent) {
      this.showError('Submissions are closed due to the sunsetting of script submissions.');
      return;
    }

    // Open author comment dialog
    const dialogData: AuthorCommentDialogData = {
      authorComment: ''
    };

    const dialogRef = this.dialog.open(AuthorCommentDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: AuthorCommentDialogResult | undefined) => {
      if (result) {
        this.performSubmission(result.authorComment);
      }
    });
  }

  private performSubmission(authorComment: string) {
    this.isSubmitting = true;
    const scriptData = this.scriptEditor.getScriptDataForSubmission();
    
    // Add author comment to the script data
    scriptData.authorComment = authorComment;
    
    this.scriptService.submitScript(scriptData, false).subscribe((response: any) => {
      console.log("Script submission response:", response);
      this.isSubmitting = false;
      
      // Check if the API response indicates success
      if (response.code === 200) {
        this.showSuccess('Script submitted successfully! It will be reviewed by our team.');
        
        // Update the script ID in the editor if this was a new script
        if (response.id && !this.scriptId) {
          this.scriptId = response.id;
          this.scriptEditor.updateScriptId(response.id);
          // Update the URL without navigation
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { script_id: response.id },
            queryParamsHandling: 'merge'
          });
        }
        
        // Navigate to the script view or scripts list after a short delay
        setTimeout(() => {
          if (response.id) {
            this.router.navigate(['/scripts', response.id]);
          } else {
            this.router.navigate(['/scripts']);
          }
        }, 2000);
      } else {
        // API returned an error response - check if it's a validation error
        if (response.code === 400 && (response.error_messages || response.errors)) {
          this.showValidationErrorDialog(
            response.message || response.msg || 'Validation failed',
            response.error_messages || response.errors
          );
        } else {
          // Other types of errors (401, 403, etc.) - use snackbar
          const errorMessage = response.msg || 'Script submission failed';
          const debugInfo = response.debug ? ` (${JSON.stringify(response.debug)})` : '';
          this.showError(`${errorMessage}${debugInfo}`);
        }
      }
      
    }, (error: any) => {
      console.error("Failed to submit script:", error);
      this.isSubmitting = false;
      
      // Check if it's a 400 validation error from the API
      if (error.error && error.error.code === 400 && (error.error.error_messages || error.error.errors)) {
        this.showValidationErrorDialog(
          error.error.message || error.error.msg || 'Validation failed',
          error.error.error_messages || error.error.errors
        );
      } else {
        this.showError(this.getErrorMessage(error));
      }
    });
  }

  saveChanges() {
    if (!this.canSubmitCurrent) {
      this.showError('Submissions are closed due to the sunsetting of script submissions.');
      return;
    }

    this.isSaving = true;
    const scriptData = this.scriptEditor.getScriptDataForSubmission();
    
    this.scriptService.submitScript(scriptData, true).subscribe((response: any) => {
      console.log("Script save response:", response);
      this.isSaving = false;
      
      // Check if the API response indicates success
      if (response.code === 200) {
        this.showSuccess('Script saved as draft successfully!');
        
        // Update the script ID if this was a new script
        if (response.id && !this.scriptId) {
          this.scriptId = response.id;
          this.scriptEditor.updateScriptId(response.id);
          // Update the URL without navigation
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { script_id: response.id },
            queryParamsHandling: 'merge'
          });
        }
      } else {
        if (response.code === 401) {
          this.showError("You need to be logged in to submit a script. Your changes have not been saved.")
          return;
        }
        // API returned an error response - check if it's a validation error
        if (response.code === 400 && (response.error_messages || response.errors)) {
          this.showValidationErrorDialog(
            response.message || response.msg || 'Validation failed',
            response.error_messages || response.errors
          );
        } else {
          // Other types of errors - use snackbar
          const errorMessage = response.msg || 'Script save failed';
          const debugInfo = response.debug ? ` (${JSON.stringify(response.debug)})` : '';
          this.showError(`${errorMessage}${debugInfo}`);
        }
      }
      
    }, (error: any) => {
      console.error("Failed to save script:", error);
      this.isSaving = false;
      
      // Check if it's a 400 validation error from the API
      if (error.error && error.error.code === 400 && (error.error.error_messages || error.error.errors)) {
        this.showValidationErrorDialog(
          error.error.message || error.error.msg || 'Validation failed',
          error.error.error_messages || error.error.errors
        );
      } else {
        this.showError(this.getErrorMessage(error));
      }
    });
  }

  private showSuccess(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string) {
    this.snackBar.open(message, 'Close', {
      panelClass: ['error-snackbar']
    });
  }

  private showValidationErrorDialog(message: string, errorMessages: string[] | string | undefined) {
    // Normalize error messages to array format
    let normalizedErrors: string[] = [];
    let legacyErrors: string | undefined = undefined;
    
    if (Array.isArray(errorMessages)) {
      normalizedErrors = errorMessages;
    } else if (typeof errorMessages === 'string' && errorMessages.length > 0) {
      // Legacy format - try to split by <br> tags or newlines
      legacyErrors = errorMessages;
      normalizedErrors = errorMessages
        .split(/<br\s*\/?>/i)
        .map(e => e.replace(/<[^>]*>/g, '').trim())
        .filter(e => e.length > 0);
    }
    
    const dialogData: ScriptSubmissionErrorData = {
      message: message,
      errorMessages: normalizedErrors,
      errors: legacyErrors
    };
    
    this.dialog.open(ScriptSubmissionErrorComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: false
    });
  }

  private getErrorMessage(error: any): string {
    if (error.apiMessage) {
      return error.apiMessage;
    }
    
    if (error.message) {
      return error.message;
    }
    
    if (error.status === 400) {
      return 'Invalid script data. Please check all required fields.';
    }
    
    if (error.status === 401) {
      return 'Please log in to submit scripts.';
    }
    
    if (error.status === 403) {
      return 'You do not have permission to submit this script.';
    }
    
    if (error.status === 410) {
      return 'Submissions are now closed due to the sunsetting of script submissions.';
    }
    
    if (error.status === 500) {
      return 'Server error. Please try again later.';
    }
    
    return 'An unexpected error occurred. Please try again.';
  }
}
